/**
 * Weekly Schedule Grid Component
 * Visual weekly template editor
 */

import { useState, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent, Button, Input } from '../ui';
import { Clock, Plus, X, Save } from 'lucide-react';
import { cn } from '../../utils/cn';
import type { WeeklyTimeSlot, DayOfWeek } from '../../types';
import { useCan } from '../../contexts/useAuth';

interface WeeklyScheduleGridProps {
  schedule: WeeklyTimeSlot[];
  onSave: (schedule: WeeklyTimeSlot[]) => Promise<void>;
  isEditable?: boolean;
  isSaving?: boolean;
}

const DAYS: { key: DayOfWeek; label: string }[] = [
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
  { key: 'sun', label: 'Sunday' },
];

const DEFAULT_START = '09:00';
const DEFAULT_END = '18:00';

export default function WeeklyScheduleGrid({
  schedule,
  onSave,
  isEditable = true,
  isSaving,
}: WeeklyScheduleGridProps) {
  const can = useCan();
  const [editedSchedule, setEditedSchedule] = useState<WeeklyTimeSlot[]>(schedule);
  const [hasChanges, setHasChanges] = useState(false);

  // Reset internal state when schedule prop changes (e.g., when switching drivers)
  const [prevSchedule, setPrevSchedule] = useState(schedule);
  if (schedule !== prevSchedule) {
    setPrevSchedule(schedule);
    setEditedSchedule(schedule);
    setHasChanges(false);
  }

  // Get slot for a specific day
  const getSlotForDay = (day: DayOfWeek): WeeklyTimeSlot | undefined => {
    return editedSchedule.find((slot) => slot.day === day && slot.is_available);
  };

  // Toggle day availability
  const toggleDay = useCallback((day: DayOfWeek) => {
    setEditedSchedule((prev) => {
      const existing = prev.find((slot) => slot.day === day);
      if (existing) {
        // Toggle is_available or remove if already unavailable
        if (existing.is_available) {
          return prev.map((slot) =>
            slot.day === day ? { ...slot, is_available: false } : slot
          );
        } else {
          return prev.map((slot) =>
            slot.day === day ? { ...slot, is_available: true } : slot
          );
        }
      } else {
        // Add the day with default times
        const newSlot: WeeklyTimeSlot = {
          day,
          start: DEFAULT_START,
          end: DEFAULT_END,
          is_available: true,
        };
        return [...prev, newSlot];
      }
    });
    setHasChanges(true);
  }, []);

  // Update time for a day
  const updateTime = useCallback((day: DayOfWeek, field: 'start' | 'end', value: string) => {
    setEditedSchedule((prev) => {
      return prev.map((slot) => {
        if (slot.day === day) {
          return { ...slot, [field]: value };
        }
        return slot;
      });
    });
    setHasChanges(true);
  }, []);

  // Handle save
  const handleSave = useCallback(async () => {
    await onSave(editedSchedule);
    setHasChanges(false);
  }, [editedSchedule, onSave]);

  // Reset changes
  const handleReset = useCallback(() => {
    setEditedSchedule(schedule);
    setHasChanges(false);
  }, [schedule]);

  return (
    <Card padding="none">
      <CardHeader className="px-4 pt-4 pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Weekly Schedule</CardTitle>
          {hasChanges && isEditable && (
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={handleReset}>
                Reset
              </Button>
              {can('schedules.manage') && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSave}
                  loading={isSaving}
                  leftIcon={<Save className="h-4 w-4" />}
                >
                  Save
                </Button>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        <div className="space-y-2">
          {DAYS.map(({ key, label }) => {
            const slot = getSlotForDay(key);
            const isActive = !!slot;

            return (
              <div
                key={key}
                className={cn(
                  'flex items-center gap-3 p-3 rounded-lg border transition-colors',
                  isActive
                    ? 'border-green-200 bg-green-50'
                    : 'border-gray-200 bg-gray-50'
                )}
              >
                {/* Day Toggle */}
                <button
                  onClick={() => isEditable && toggleDay(key)}
                  disabled={!isEditable}
                  className={cn(
                    'w-24 text-left font-medium text-sm',
                    isActive ? 'text-green-700' : 'text-gray-500',
                    isEditable && 'cursor-pointer hover:opacity-80'
                  )}
                >
                  {label}
                </button>

                {/* Time Inputs or Off Label */}
                {isActive ? (
                  <div className="flex items-center gap-2 flex-1">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <Input
                      type="time"
                      value={slot.start}
                      onChange={(e) => updateTime(key, 'start', e.target.value)}
                      disabled={!isEditable}
                      size="sm"
                      className="w-28"
                    />
                    <span className="text-gray-500">to</span>
                    <Input
                      type="time"
                      value={slot.end}
                      onChange={(e) => updateTime(key, 'end', e.target.value)}
                      disabled={!isEditable}
                      size="sm"
                      className="w-28"
                    />
                    {isEditable && (
                      <button
                        onClick={() => toggleDay(key)}
                        className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 flex-1">
                    <span className="text-sm text-gray-500">Day off</span>
                    {isEditable && (
                      <button
                        onClick={() => toggleDay(key)}
                        className="p-1 text-gray-400 hover:text-green-500 hover:bg-green-50 rounded"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Summary */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Working days</span>
            <span className="font-medium text-gray-900">
              {editedSchedule.length} / 7
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
